import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, ShieldCheck } from 'lucide-react';

const Header = ({ user, isAdmin, onLoginClick, onSignupClick, onLogoutClick }) => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const navigate = useNavigate();

    const handleLogoutAndNavigate = async () => {
        await onLogoutClick();
        setIsMobileMenuOpen(false);
        navigate('/');
    };

    const handleLinkClick = (path) => {
        setIsMobileMenuOpen(false);
        navigate(path);
    };

    return (
        <>
        <nav className="bg-white shadow-sm py-4 px-6 lg:px-12 flex justify-between items-center sticky top-0 z-50">
            <Link to="/" className="flex items-center space-x-2">
                <img src="/ArushaLife_Logo.png" alt="Arusha Life Logo" className="h-10 w-auto" /> {/* Replaced placeholder div with logo image */}
                <div className="flex flex-col">
                    <span className="text-xl font-bold text-gray-800 leading-none tracking-tight">Arusha<span style={{color: '#3D550C'}}>Life</span></span>
                    <span className="text-[10px] text-gray-500 font-medium tracking-wider uppercase">The City's Pulse</span>
                </div>
            </Link>

            {/* Mobile menu button */}
            <div className="lg:hidden">
                <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-gray-600 focus:outline-none">
                    {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
                </button>
            </div>

            {/* Desktop navigation */}
            <div className="hidden lg:flex items-center space-x-6"> 
                <Link to="/directory" className="text-gray-600 hover:text-green-800 transition-colors">Directory</Link> 
                <Link to="/map" className="text-gray-600 hover:text-green-800 transition-colors">Map</Link>
                <Link to="/events" className="text-gray-600 hover:text-green-800 transition-colors">Events</Link>
                <Link to="/community" className="text-gray-600 hover:text-green-800 transition-colors font-semibold">Community</Link>
                <Link to="/news" className="text-gray-600 hover:text-green-800 transition-colors">News</Link>
                <Link to="/guides" className="text-gray-600 hover:text-green-800 transition-colors">Guides</Link>
                <Link to="/classifieds" className="text-gray-600 hover:text-green-800 transition-colors">Classifieds</Link>
                {isAdmin && (
                    <Link to="/admin" className="text-blue-600 hover:text-blue-800 transition-colors font-medium flex items-center">
                        <ShieldCheck size={16} className="mr-1" /> Admin
                    </Link>
                )}
                {user && (
                    <>
                        <Link to="/favorites" className="text-gray-600 hover:text-green-800 transition-colors">My Favorites</Link>
                    </>
                )} 
            </div> 
            <div className="hidden lg:flex items-center space-x-4"> 
                <Link to="/create-ad" className="text-brand-primary border border-brand-primary px-4 py-2 rounded-md hover:bg-green-50 transition-colors font-semibold">
                    Advertise
                </Link>
                {user ? ( 
                    <> 
                        <Link to="/profile" className="flex items-center space-x-2 mr-4">
                            {user.photoURL && <img src={user.photoURL} alt="Avatar" className="w-8 h-8 rounded-full border border-gray-200" />}
                            <span className="text-gray-700 text-sm font-medium hover:text-green-800">{user.displayName || user.email}</span> 
                        </Link>
                        <button onClick={handleLogoutAndNavigate} className="text-gray-600 hover:text-green-800 px-4 py-2 rounded-md transition-colors text-sm border hover:border-gray-300">Log Out</button> 
                    </> 
                ) : ( 
                    <> 
                        <button onClick={onLoginClick} className="text-gray-600 hover:text-green-800 px-4 py-2 rounded-md transition-colors">Log In</button> 
                        <button onClick={onSignupClick} className="bg-primary-green text-white px-4 py-2 rounded-md" style={{backgroundColor: '#3D550C'}}>Sign Up</button> 
                    </> 
                )} 
            </div>
        </nav>

        {/* Mobile Menu Overlay */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 bg-white z-30 flex flex-col items-center py-6 shadow-lg">
            <div className="flex flex-col space-y-4 mt-8">
              {isAdmin && <button onClick={() => handleLinkClick('/admin')} className="text-blue-600 text-lg font-bold flex items-center justify-center"><ShieldCheck size={20} className="mr-2"/> Admin Dashboard</button>}
              <button onClick={() => handleLinkClick('/directory')} className="text-gray-800 text-lg font-medium hover:text-green-800">Directory</button>
              <button onClick={() => handleLinkClick('/map')} className="text-gray-800 text-lg font-medium hover:text-green-800">Map</button>
              <button onClick={() => handleLinkClick('/events')} className="text-gray-800 text-lg font-medium hover:text-green-800">Events</button>
              <button onClick={() => handleLinkClick('/community')} className="text-gray-800 text-lg font-medium hover:text-green-800">Community</button>
              <button onClick={() => handleLinkClick('/news')} className="text-gray-800 text-lg font-medium hover:text-green-800">News</button>
              <button onClick={() => handleLinkClick('/guides')} className="text-gray-800 text-lg font-medium hover:text-green-800">Guides</button>
              <button onClick={() => handleLinkClick('/classifieds')} className="text-gray-800 text-lg font-medium hover:text-green-800">Classifieds</button>
              <button onClick={() => handleLinkClick('/create-ad')} className="text-brand-primary text-lg font-bold border border-brand-primary px-6 py-2 rounded-md">Advertise</button>
              {user && (
                <>
                  <button onClick={() => handleLinkClick('/favorites')} className="text-gray-800 text-lg font-medium hover:text-green-800">My Favorites</button>
                  <button onClick={() => handleLinkClick('/profile')} className="text-gray-800 text-lg font-medium hover:text-green-800">Profile</button>
                </>
              )}
            </div>
            
            <div className="mt-8 flex flex-col space-y-3 w-64">
                {user ? (
                    <button onClick={handleLogoutAndNavigate} className="w-full border border-gray-300 text-gray-700 py-3 rounded-md font-medium">Log Out</button>
                ) : (
                    <>
                        <button onClick={() => { setIsMobileMenuOpen(false); onLoginClick(); }} className="w-full border border-gray-300 text-gray-700 py-3 rounded-md font-medium">Log In</button>
                        <button onClick={() => { setIsMobileMenuOpen(false); onSignupClick(); }} className="w-full bg-[#3D550C] text-white py-3 rounded-md font-bold shadow-md">Sign Up</button>
                    </>
                )}
            </div>
          </div>
        )}
        </>
    );
};

export default Header;
